import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import './Home.css';
import ActivityMap from '../components/ActivityMap';
import {
  ShieldCheck,
  Lock,
  ArrowRight,
  Check,
  X,
  Droplets,
  Plug,
  Snowflake,
  HardHat,
  Hammer,
  Plus,
  Star,
  Headset,
  DollarSign,
  Clock,
  Phone,
  Building2
} from 'lucide-react';

// Shared styles for the homepage trust-boxes block that replaces the empty
// testimonials state. Defined at module scope so the objects are stable.
const trustBoxStyle = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderTop: '3px solid #F97316',
  borderRadius: 12,
  padding: '1.5rem 1.25rem',
  textAlign: 'left',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};
const trustIconStyle = { color: '#F97316', marginBottom: 12 };
const trustH3Style = {
  margin: '0 0 8px 0',
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#0f172a',
  lineHeight: 1.3
};
const trustPStyle = {
  margin: 0,
  color: '#475569',
  fontSize: '0.92rem',
  lineHeight: 1.5
};

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const observerOptions = { root: null, rootMargin: '0px', threshold: 0.1 };
    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));
    return () => { animatedElements.forEach((el) => observer.unobserve(el)); };
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_URL}/api/reviews/public/testimonials?limit=6`);
        if (response.ok) {
          const data = await response.json();
          if (data.testimonials && data.testimonials.length > 0) {
            setTestimonials(data.testimonials);
          }
        }
      } catch (error) { /* Show empty-state copy when API unavailable */ }
    };
    fetchTestimonials();
  }, []);

  // Real testimonials only — no fake placeholder fallback. If fewer than 3,
  // we show what's available without auto-rotation. If zero, the section
  // renders an honest empty-state message instead.
  const hasTestimonials = testimonials.length > 0;
  const canRotate = testimonials.length >= 3;

  const nextTestimonial = useCallback(() => {
    if (!canRotate) return;
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  }, [canRotate, testimonials.length]);

  useEffect(() => {
    if (!canRotate) return undefined;
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, [nextTestimonial, canRotate]);

  const getVisibleTestimonials = () => {
    if (!hasTestimonials) return [];
    if (!canRotate) return testimonials;
    const result = [];
    for (let i = 0; i < 3; i++) {
      result.push(testimonials[(currentTestimonial + i) % testimonials.length]);
    }
    return result;
  };

  return (
    <>
      <Helmet>
        <title>Real Time Repairs | Stop Chasing Contractors. Let Them Come to You.</title>
        <meta name="description" content="Post your home repair in 2 minutes. Verified contractors compete for your job with real quotes. Free for homeowners, forever. No calls, no hassle." />
        <link rel="canonical" href="https://www.realtimerepairs.com/" />
        <meta property="og:title" content="Real Time Repairs | Stop Chasing Contractors" />
        <meta property="og:description" content="Post your repair. Get competing quotes from verified pros. Completely free." />
        <meta property="og:url" content="https://www.realtimerepairs.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.realtimerepairs.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Real Time Repairs | Stop Chasing Contractors" />
        <meta name="twitter:description" content="Post your repair. Get competing quotes from verified pros. Completely free." />
        <meta name="twitter:image" content="https://www.realtimerepairs.com/og-image.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Real Time Repairs",
            "alternateName": "RTR",
            "url": "https://www.realtimerepairs.com",
            "description": "A contractor marketplace where homeowners post repair jobs and verified contractors compete with real quotes.",
            "foundingDate": "2025",
            "areaServed": { "@type": "Country", "name": "United States" },
            "knowsAbout": ["HVAC Repair", "Plumbing Services", "Electrical Services", "Roofing", "Home Repairs"],
            "slogan": "Stop Chasing Contractors. Let Them Come to You."
          }
        `}</script>
      </Helmet>

      <main className="home-page">

        {/* ===================== HERO ===================== */}
        <section className="hero-clean">
          <div className="hero-clean-inner">
            <div className="hero-clean-content">
              <h1>Stop Calling Contractors<br />Who Never <span className="text-accent">Call Back.</span></h1>
              <p className="hero-clean-subtitle">
                Post your repair once. Verified contractors see it and send you real quotes.
                You compare, you choose, you're done. No phone tag. No runaround.
              </p>
              <a
                href="https://app.realtimerepairs.com/quick-post"
                className="btn btn-accent btn-large hero-btn"
                onClick={() => {
                  if (typeof window.gtag === 'function') {
                    try { window.gtag('event', 'home_hero_post_click', { page_path: '/' }); } catch (e) { /* analytics never blocks UX */ }
                  }
                }}
              >
                Post Your Repair — It's Free
                <ArrowRight size={18} />
              </a>
              <div className="hero-proof">
                <span><Clock size={14} /> Takes 2 minutes</span>
                <span><DollarSign size={14} /> 100% free, forever</span>
                <span><ShieldCheck size={14} /> Every pro is verified</span>
              </div>
            </div>
            <div className="hero-clean-image">
              <picture>
                <source
                  srcSet="/hero-image-mobile.webp 600w, /hero-image-tablet.webp 900w, /hero-image-compressed.webp 1200w"
                  sizes="(max-width: 600px) 600px, (max-width: 900px) 900px, 1200px"
                  type="image/webp"
                />
                <img
                  src="/hero-image-optimized.png"
                  alt="Homeowner receiving contractor quotes on their phone"
                  width="1200"
                  height="800"
                  fetchpriority="high"
                />
              </picture>
            </div>
          </div>
        </section>

        {/* ===================== THE PROBLEM ===================== */}
        <section className="problem-section">
          <div className="problem-inner">
            <div className="problem-left animate-on-scroll">
              <h2>Sound familiar?</h2>
              <ul className="problem-list">
                <li><X size={18} className="icon-red" /> You Google "plumber near me" and get 50 results</li>
                <li><X size={18} className="icon-red" /> You call 10 of them. Maybe 2 pick up.</li>
                <li><X size={18} className="icon-red" /> You leave voicemails. Nobody calls back.</li>
                <li><X size={18} className="icon-red" /> A week later, you still don't have a quote.</li>
                <li><X size={18} className="icon-red" /> You hire whoever answers and hope for the best.</li>
              </ul>
            </div>
            <div className="problem-right animate-on-scroll delay-200">
              <h2>Now imagine this:</h2>
              <ul className="solution-list">
                <li><Check size={18} className="icon-green" /> You describe your problem in 2 minutes.</li>
                <li><Check size={18} className="icon-green" /> Verified contractors in your area see it instantly.</li>
                <li><Check size={18} className="icon-green" /> They send you real quotes. You didn't call anyone.</li>
                <li><Check size={18} className="icon-green" /> You compare bids, check reviews, see credentials.</li>
                <li><Check size={18} className="icon-green" /> You pick the best one. Done.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ===================== WHY FREE ===================== */}
        <section className="why-free">
          <div className="why-free-inner animate-on-scroll">
            <p className="why-free-text">
              <strong>"Wait, this is actually free?"</strong> — Yes. Contractors pay us to access jobs.
              You never pay us a dime. Not now, not ever.
            </p>
          </div>
        </section>

        {/* ===================== HOW IT WORKS ===================== */}
        <section className="how-it-works">
          <div className="how-it-works-inner">
            <h2 className="section-title animate-on-scroll">Three Steps. That's It.</h2>
            <div className="steps-row">
              <div className="step-card animate-on-scroll delay-100">
                <div className="step-number">1</div>
                <h3>Tell Us What's Broken</h3>
                <p>Leaky faucet? Busted AC? Roof damage? Describe it, snap a photo if you want. Done in 2 minutes.</p>
              </div>
              <div className="step-connector" aria-hidden="true"></div>
              <div className="step-card animate-on-scroll delay-200">
                <div className="step-number">2</div>
                <h3>Contractors Come to You</h3>
                <p>No calling around. Verified pros in your area see your job and send you competing quotes. You just wait.</p>
              </div>
              <div className="step-connector" aria-hidden="true"></div>
              <div className="step-card animate-on-scroll delay-300">
                <div className="step-number">3</div>
                <h3>Pick the Best One</h3>
                <p>Compare bids side by side. Check their reviews. See their credentials. Hire the one you trust. Pay them directly — we don't take a cut.</p>
              </div>
            </div>
            <div className="steps-cta animate-on-scroll">
              <a href="https://app.realtimerepairs.com/quick-post" className="btn btn-accent btn-large">
                Post Your Repair Now
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* ===================== SERVICES ===================== */}
        <section className="services-clean">
          <div className="services-clean-inner">
            <h2 className="section-title animate-on-scroll">What Needs Fixing?</h2>
            <div className="services-grid">
              <a href="https://app.realtimerepairs.com/quick-post" className="service-tile animate-on-scroll delay-100">
                <div className="service-tile-icon"><Droplets size={28} /></div>
                <span className="service-tile-name">Plumbing</span>
                <span className="service-tile-desc">Leaks, clogs, water heaters</span>
              </a>
              <a href="https://app.realtimerepairs.com/quick-post" className="service-tile animate-on-scroll delay-100">
                <div className="service-tile-icon"><Plug size={28} /></div>
                <span className="service-tile-name">Electrical</span>
                <span className="service-tile-desc">Outlets, panels, wiring</span>
              </a>
              <a href="https://app.realtimerepairs.com/quick-post" className="service-tile animate-on-scroll delay-200">
                <div className="service-tile-icon"><Snowflake size={28} /></div>
                <span className="service-tile-name">HVAC</span>
                <span className="service-tile-desc">AC, heating, ventilation</span>
              </a>
              <a href="https://app.realtimerepairs.com/quick-post" className="service-tile animate-on-scroll delay-200">
                <div className="service-tile-icon"><HardHat size={28} /></div>
                <span className="service-tile-name">Roofing</span>
                <span className="service-tile-desc">Leaks, damage, replacements</span>
              </a>
              <a href="https://app.realtimerepairs.com/quick-post" className="service-tile animate-on-scroll delay-300">
                <div className="service-tile-icon"><Hammer size={28} /></div>
                <span className="service-tile-name">Handyman</span>
                <span className="service-tile-desc">Odd jobs, repairs, installs</span>
              </a>
              <a href="https://app.realtimerepairs.com/quick-post" className="service-tile animate-on-scroll delay-300">
                <div className="service-tile-icon"><Plus size={28} /></div>
                <span className="service-tile-name">50+ More</span>
                <span className="service-tile-desc">Painting, landscaping, you name it</span>
              </a>
            </div>
          </div>
        </section>

        {/* ===================== SOCIAL PROOF / TRUST ===================== */}
        <section className="testimonials-clean">
          <div className="testimonials-clean-inner">
            <h2 className="section-title animate-on-scroll">
              {hasTestimonials ? "Don't Take Our Word for It" : 'Built for Real Homeowners'}
            </h2>
            {hasTestimonials ? (
              <div className="testimonials-grid animate-on-scroll delay-100">
                {getVisibleTestimonials().map((testimonial, index) => {
                  const displayName = testimonial.homeowner_name || testimonial.name || '';
                  const displayLocation = testimonial.location ||
                    (testimonial.city && testimonial.state
                      ? `${testimonial.city}, ${testimonial.state}`
                      : '');
                  const initial = (displayName || '?').trim().charAt(0).toUpperCase() || '?';
                  return (
                    <div key={index} className="testimonial-card">
                      <div className="testimonial-stars">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} size={16} fill="#F97316" stroke="#F97316" />
                        ))}
                      </div>
                      <p className="testimonial-text">"{testimonial.text || testimonial.review_text}"</p>
                      <div className="testimonial-author">
                        <div
                          aria-hidden="true"
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #f97316, #fb923c)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 18,
                            flexShrink: 0,
                          }}
                        >
                          {initial}
                        </div>
                        <div>
                          <div className="testimonial-name">{displayName}</div>
                          {displayLocation && (
                            <div className="testimonial-location">{displayLocation}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="animate-on-scroll delay-100"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '1.25rem',
                  maxWidth: 1100,
                  margin: '0 auto',
                  padding: '0 1rem',
                }}
              >
                <div style={trustBoxStyle}>
                  <DollarSign size={28} style={trustIconStyle} />
                  <h3 style={trustH3Style}>Free for homeowners</h3>
                  <p style={trustPStyle}>Post a repair, compare quotes, hire a pro — we never charge you a dime.</p>
                </div>
                <div style={trustBoxStyle}>
                  <ShieldCheck size={28} style={trustIconStyle} />
                  <h3 style={trustH3Style}>Verified local contractors</h3>
                  <p style={trustPStyle}>We check credentials before contractors can see and bid on your job.</p>
                </div>
                <div style={trustBoxStyle}>
                  <Phone size={28} style={trustIconStyle} />
                  <h3 style={trustH3Style}>Post once instead of calling around</h3>
                  <p style={trustPStyle}>One short form replaces a dozen voicemails to contractors who never call back.</p>
                </div>
                <div style={trustBoxStyle}>
                  <Building2 size={28} style={trustIconStyle} />
                  <h3 style={trustH3Style}>Homes, rentals, businesses &amp; property managers</h3>
                  <p style={trustPStyle}>Your house, a rental unit, or a whole portfolio — same fast intake works for all of it.</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ===================== OBJECTION KILLER ===================== */}
        <section className="objections">
          <div className="objections-inner">
            <h2 className="section-title animate-on-scroll">Still on the Fence?</h2>
            <div className="objections-grid">
              <div className="objection-card animate-on-scroll delay-100">
                <DollarSign size={24} className="objection-icon" />
                <h3>It's free. Actually free.</h3>
                <p>No hidden fees, no credit card, no "free trial" that charges you later. Contractors pay us — you don't.</p>
              </div>
              <div className="objection-card animate-on-scroll delay-200">
                <ShieldCheck size={24} className="objection-icon" />
                <h3>Every contractor is verified.</h3>
                <p>We check credentials before contractors can bid on your job. No randos showing up at your door.</p>
              </div>
              <div className="objection-card animate-on-scroll delay-300">
                <Phone size={24} className="objection-icon" />
                <h3>Zero phone calls required.</h3>
                <p>Post your job, get quotes, message contractors, hire — all from your phone. No voicemail tag.</p>
              </div>
              <div className="objection-card animate-on-scroll delay-300">
                <Lock size={24} className="objection-icon" />
                <h3>No obligation. Walk away anytime.</h3>
                <p>Don't like the quotes? Don't hire anyone. No pressure, no guilt, no follow-up spam.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== ROLLOUT MAP ===================== */}
        <ActivityMap />

        {/* ===================== FINAL CTA ===================== */}
        <section className="cta-clean">
          <div className="cta-clean-inner animate-on-scroll">
            <h2>Your Weekend Shouldn't Disappear<br />Into a List of Contractor Phone Numbers.</h2>
            <p>Post your repair in 2 minutes. Let the pros come to you.</p>
            <a href="https://app.realtimerepairs.com/quick-post" className="btn btn-accent btn-large">
              Get Free Quotes Now
              <ArrowRight size={18} />
            </a>
            <div className="cta-trust-row">
              <span><ShieldCheck size={16} /> Verified contractors</span>
              <span><DollarSign size={16} /> Free forever</span>
              <span><Headset size={16} /> Real human support</span>
            </div>
          </div>
        </section>

        {/* ===================== CONTRACTOR BANNER ===================== */}
        <section className="contractor-banner">
          <div className="contractor-banner-inner">
            <span><HardHat size={20} /> <strong>Are you a contractor?</strong> Browse local jobs. No lead fees. Free during beta.</span>
            <a href="https://app.realtimerepairs.com/register?type=contractor" className="btn btn-outline-light">
              Join Free <ArrowRight size={16} />
            </a>
          </div>
        </section>

      </main>
    </>
  );
};

export default Home;
